import { Link } from "react-router-dom";
import { MapPin, Users, Wrench, Globe, ShoppingBag, GraduationCap, Stethoscope, Fuel, Scissors, Mail, Bus, Building2, Trash2, Shield, Phone, Info } from "lucide-react";
import { MapLink } from "@/components/MapLink";
import { OrnamentDivider, VineOrnament } from "@/components/Ornaments";

export default function NyIBygden() {
  return (
    <div data-testid="ny-i-bygden-page" className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      {/* Dekorativa rankor i kanterna */}
      <VineOrnament className="hidden lg:block absolute top-10 -left-20 w-16 h-48 text-gold-deep opacity-35" />
      <VineOrnament className="hidden lg:block absolute top-10 -right-20 w-16 h-48 text-gold-deep opacity-35" flip />

      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Guide</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3">Ny här? Det här är bra att veta.</h1>
      <OrnamentDivider className="text-gold-deep opacity-50 my-3" />
      <p className="text-sm text-bark mb-10 max-w-2xl leading-relaxed">
        Att flytta till Backe, Rossön eller någon av byarna längs Fjällsjöälven handlar om mer än en ny adress.
        Här har vi samlat det som ingen tänker på att berätta men som alla behöver veta &ndash; från var man köper mjölk
        till hur sopsorteringen fungerar.
      </p>

      <div className="flex flex-col gap-8">

        {/* Dagligvaror */}
        <InfoSection icon={ShoppingBag} title="Mataffärer och dagligvaror">
          <p>Bygden har två <strong>ICA Nära</strong>-butiker, en i Backe (Centrumvägen 7) och en i Rossön (Hotingsvägen 7).
            Båda har post- och pakethämtning (PostNord). <strong>Systembolagets ombud</strong> finns både i Backe
            (ICA Nära Backe) och i Rossön (ICA Nära Rossön) &ndash; beställ via systembolaget.se och hämta i butiken.</p>
          <p>För Systembolagets hela sortiment över disk, eller större kedjor som Coop och Lidl, kör man till
            <strong> Strömsund</strong> (ca 45 minuter).</p>
          <div className="flex flex-wrap gap-3 mt-1">
            <MapLink name="ICA Nära Backe" />
            <MapLink name="ICA Nära Rossön" />
          </div>
        </InfoSection>

        {/* Apotek & hälsa */}
        <InfoSection icon={Stethoscope} title="Apotek, hälsa och vård">
          <p><strong>Apoteket</strong> har lokal verksamhet i Backe (Centrumvägen 7, samma byggnad som ICA Nära Backe).
            I Rossön finns apoteksombud via ICA Nära Rossön &ndash; beställning via apoteket.se, hämtning i butiken.</p>
          <p><strong>Backe hälsocentral</strong> på Lasarettsvägen 20 har vårdcentral och vissa specialiserade tjänster.
            Ring alltid <a href="tel:1177" className="text-falu hover:text-falu-hover underline">1177</a> för rådgivning dygnet runt,
            eller <a href="tel:112" className="text-falu hover:text-falu-hover underline">112</a> vid akut nöd.</p>
          <p><strong>Fothälsa</strong> hos Amanda Sjödin finns i Backe (tidigare SCA-kontoret vid torget).
            <strong> Fjällveterinären</strong> i Strömsund tar emot husdjur och lantbruksdjur.</p>
          <p><strong>Tandvård</strong>: närmaste tandklinik finns i Strömsund.</p>
        </InfoSection>

        {/* Bensin / drivmedel */}
        <InfoSection icon={Fuel} title="Drivmedel och bilservice">
          <p>Två lokala <strong>mackar</strong>:</p>
          <ul className="list-disc pl-5">
            <li>Mack Backe &ndash; vid ortseingången, mittemot NISAB.</li>
            <li>Mack Rossön &ndash; direkt före bron, ca 100 m från ICA.</li>
          </ul>
          <p>Båda är i första hand automatstationer. För full service (verkstad, elbilsladdning) är närmaste alternativ
            OKQ8 Strömsund-Näsviken (ca 4,5 mil).</p>
          <p><strong>Bilservice</strong>: Rossön Däckservice AB (Mekopartner) och Rossöns Järn &amp; Motor i Rossön.</p>
        </InfoSection>

        {/* Frisör, skönhet */}
        <InfoSection icon={Scissors} title="Frisör och personlig service">
          <p><strong>Ronjas Hårdesign</strong> &ndash; frisörsalong i Backe, Läkarvägen 18. För större frisyrbyten eller herrklippning
            finns flera salonger i Strömsund.</p>
          <p><strong>Fothälsa</strong> &ndash; Amanda Sjödin i Backe.</p>
          <p><strong>Loppis/Second hand</strong>: Loppisbröderna i Backe (Läkarvägen 20) och Loppis via Backe IF vid Stenhammaren under sommarmånaderna.</p>
        </InfoSection>

        {/* Skola, barnomsorg */}
        <InfoSection icon={GraduationCap} title="Skola och barnomsorg">
          <p><strong>Fjällsjöskolan (F&ndash;9)</strong> på Läkarvägen 22 i Backe är kommunal grundskola med ca 110&ndash;120 elever.
            Rektor: Susanne Sjödin. Tel: <a href="tel:0727069324" className="text-falu hover:text-falu-hover underline">072-706 93 24</a>.</p>
          <p><strong>Förskola</strong>: Riddarens förskola på Lasarettsvägen 9 i Backe (rektor Ann-Kristin Lindkvist, tel
            <a href="tel:0671716615" className="text-falu hover:text-falu-hover underline"> 0671-71 66 15</a>).</p>
          <p>Gymnasium finns i Strömsund (Hjalmar Strömerskolan) med skolskjuts från bygden.</p>
        </InfoSection>

        {/* Post och paket */}
        <InfoSection icon={Mail} title="Post och paket">
          <p>Både <strong>Backe och Rossön</strong> har PostNord-ombud på respektive ICA Nära.
            Paket hämtas och skickas där, och brevlådor finns utanför butikerna.</p>
          <p><strong>DHL och Schenker</strong> har lokalt utlämningsställe i Backe (Nr 72).
            För större paket eller annan operatör finns servicepunkter även i Strömsund.</p>
        </InfoSection>

        {/* Bank, uttagsautomat */}
        <InfoSection icon={Building2} title="Bank och uttagsautomat">
          <p><strong>Bankomat</strong> finns på Torgvägen 1A i Backe (samma hus som byservicekontoret).
            Närmaste fysiska bankkontor ligger i Strömsund.</p>
          <p><strong>Byservicekontoret Backe</strong> på Torgvägen 1A säljer fiskekort, delar ut FjällsjöBladet och
            hjälper till med lokala frågor. Tel: <a href="tel:062410169" className="text-falu hover:text-falu-hover underline">0624-101 69</a>.
            Öppet mån/ons/fre 9&ndash;12, tis/tor 9&ndash;15.</p>
        </InfoSection>

        {/* Buss och transport */}
        <InfoSection icon={Bus} title="Buss och transport">
          <p><strong>Backe busstation</strong> (hållplats mitt i byn) har lokaltrafik och länstrafikbussar mot Strömsund,
            Östersund och Hoting. Sök tidtabeller via <a href="https://www.dintur.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">DinTur</a>.</p>
          <p>Egen bil är i praktiken nödvändigt för de flesta i bygden, särskilt under vinterhalvåret.</p>
        </InfoSection>

        {/* Sopor, återvinning */}
        <InfoSection icon={Trash2} title="Sopsortering och återvinning">
          <p><strong>Hushållsavfall</strong> hämtas varannan vecka &ndash; ställ ut kärlet senast kl 06.
            Schema och abonnemang via <a href="https://www.stromsund.se/7541.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Strömsunds kommun</a>.</p>
          <p><strong>Återvinningsstationer (förpackningar)</strong>: Ringvägen i Rossön och flera platser i Backe
            (glas, plast, papper, metall).</p>
          <p><strong>Återvinningscentralen (ÅVC)</strong> ligger på väg 346 mellan Backe och Rossön, öppen tisdagar 12&ndash;18.
            Här lämnar du grovsopor och farligt avfall. Kundtjänst: <a href="tel:067016417" className="text-falu hover:text-falu-hover underline">0670-164 17</a>.</p>
        </InfoSection>

        {/* Säkerhet, brand */}
        <InfoSection icon={Shield} title="Säkerhet och nödnummer">
          <p><strong>Räddningstjänsten Backe</strong> på Kuskvägen 4 är den lokala brandstationen.</p>
          <p>Vid akut nöd, brand eller allvarlig olycka &ndash; ring alltid <a href="tel:112" className="text-falu hover:text-falu-hover underline">112</a>.
            För sjukvårdsrådgivning: <a href="tel:1177" className="text-falu hover:text-falu-hover underline">1177</a> dygnet runt.</p>
          <p>I glesbygd kan utryckningstiden vara längre &ndash; lokala grannar är ofta första hjälpen. Lär känna dem tidigt.</p>
        </InfoSection>

        {/* Internet, mobiltäckning */}
        <InfoSection icon={Phone} title="Bredband och mobiltäckning">
          <p><strong>Fiber</strong> finns utbyggt i delar av Backe och Rossön. Andra områden har 4G/5G som primär anslutning.
            Kolla med din operatör för aktuell täckning.</p>
          <p>Mobilen kan tappa täckning i skogen och på vissa bakvägar &ndash; ha alltid karta och plan B vid längre utflykter.</p>
        </InfoSection>

        {/* Övrig hjälp */}
        <InfoSection icon={Wrench} title="Hjälp i vardagen">
          <p>Behöver du hjälp med ved, snöröjning, flyttlass eller skjuts? Kolla <Link to="/anslagstavla/hjalp-i-vardagen" className="text-falu hover:text-falu-hover underline">anslagstavlan</Link>.</p>
          <p>Strömsunds kommun hanterar vatten, avlopp, bygglov och barnomsorg. Telefon:
            <a href="tel:067016100" className="text-falu hover:text-falu-hover underline"> 0670-161 00</a>.</p>
          <p>Lokala nyheter: <strong>FjällsjöBladet</strong> utkommer varannan torsdag. Se även <a href="http://backe.nu" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">backe.nu</a>.</p>
        </InfoSection>

        {/* Gemenskap */}
        <InfoSection icon={Users} title="Föreningsliv och gemenskap">
          <p>Rossöns IF har cirka 420 medlemmar &ndash; i Bodum med bara 300 invånare. Skidor, hockey, simskola,
            frisbeegolf, löptävlingar och bingo. Backe IF driver Stenhammarens camping och skidspår i Backe.</p>
          <p>Hembygdsföreningar i både Fjällsjö och Bodum arrangerar marknader, spelmansstämmor och traditionella högtider.
            Kontakt till Bodums hembygdsförening: <a href="tel:+46705098058" className="text-falu hover:text-falu-hover underline">070-509 80 58</a>.</p>
          <p>Se <Link to="/foreningar-evenemang" className="text-falu hover:text-falu-hover underline">alla föreningar och evenemang</Link>.</p>
        </InfoSection>

        {/* Ny i Sverige? */}
        <InfoSection icon={Globe} title="Ny i Sverige?">
          <p>Har du nyligen flyttat till Sverige? Här är de viktigaste stegen.</p>
          <ul className="list-disc pl-5 flex flex-col gap-2">
            <li><a href="https://www.skatteverket.se/privat/folkbokforing/flytta/flyttatillsverige.4.76a43be412206334b89800018181.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Skatteverket</a> &ndash; folkbokföring och personnummer.</li>
            <li><a href="https://www.migrationsverket.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Migrationsverket</a> &ndash; uppehålls- och arbetstillstånd.</li>
            <li><a href="https://www.informationsverige.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Informationsverige.se</a> &ndash; guide om livet i Sverige på flera språk.</li>
            <li><strong>SFI</strong> &ndash; gratis svenskundervisning via <a href="https://www.stromsund.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Strömsunds kommun</a>.</li>
            <li><a href="https://www.forsakringskassan.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Försäkringskassan</a> &ndash; barnbidrag och ersättningar.</li>
          </ul>
          <p className="mt-2 text-bark">Tveka inte att fråga grannar eller skriva på <Link to="/anslagstavla/hjalp-i-vardagen" className="text-falu hover:text-falu-hover underline">anslagstavlan</Link>.</p>
        </InfoSection>

        {/* "Det alla vet och ingen berättar" – orientering för nyankomna,
            återflyttare, besökare. Ej juridisk rådgivning; bara praktisk hjälp
            att veta vart man vänder sig. */}
        <InfoSection
          icon={Info}
          title="Det alla vet och ingen berättar"
          data-testid="det-alla-vet-section"
        >
          <p className="font-heading text-base font-semibold text-ink !mb-1">Vardagskunskap som lokalbefolkningen tar för given.</p>
          <p>Är du ny här &ndash; återflyttare, inflyttad, besökare eller på väg att stanna lite längre &ndash; samlar vi praktisk
            orientering på ett ställe. Inget av detta är juridisk rådgivning, bara vägledning om vart man vänder sig
            och vad som faktiskt finns runt knuten.</p>

          <ul className="list-disc pl-5 marker:text-pine/70 flex flex-col gap-1.5 mt-2">
            <li>
              <strong>Personnummer, samordningsnummer och folkbokföring</strong> – om du flyttar hit eller stannar länge,
              kontaktar du <a href="https://www.skatteverket.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Skatteverket</a>.
              För registrering av fordon, körkort och liknande gäller <a href="https://www.transportstyrelsen.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Transportstyrelsen</a>.
            </li>
            <li>
              <strong>Var finns vad i bygden?</strong> Backe har mataffär, apotek, Systembolagets ombud, hälsocentral, skola,
              förskola och pakethämtning. Rossön har mataffär, post- och Systembolagsombud, däckservice och boenden.
              Du behöver inte åka till Strömsund för grundläggande inköp eller läkemedel.
            </li>
            <li>
              <strong>Vem frågar man?</strong> Byservicekontoret i Backe (Torgvägen 1A,
              <a href="tel:0624101 69" className="text-falu hover:text-falu-hover underline"> 0624-101 69</a>)
              är den bästa första kontakten. Där får du tips om fiskekort, lokala arrangemang,
              hantverkare och vart man vänder sig vidare.
            </li>
            <li>
              <strong>Praktiskt om bostad</strong> – Strömsunds Hyresbostäder har hyresrätter i bygden.
              Anmäl dig i bostadskön i god tid: <a href="tel:067016955" className="text-falu hover:text-falu-hover underline">0670-169 55</a>.
            </li>
            <li>
              <strong>Bra att veta:</strong> mobiltäckningen är ojämn utanför tätorterna, fiber finns i delar av Backe och Rossön,
              och sopor hämtas varannan vecka (kärlet ut senast 06:00).
            </li>
          </ul>
          <p className="text-xs text-bark/70 mt-3 italic">
            Saknar du något? Hör av dig till <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover underline">redaktion@vidalv.live</a> &ndash;
            sidan lever av lokala tillägg.
          </p>
        </InfoSection>

        {/* Kartan-hänvisning */}
        <div className="bg-bone-alt/60 border border-warm-border rounded-sm p-5 flex items-start gap-3">
          <Info size={18} className="text-pine shrink-0 mt-0.5" />
          <div>
            <p className="font-heading text-lg font-semibold text-ink mb-1">Hitta platserna på kartan</p>
            <p className="text-sm text-bark mb-2">Alla verksamheter ovan med bekräftad adress finns som numrerade pinnar på vår karta.</p>
            <Link to="/karta" data-testid="ny-link-karta" className="inline-flex items-center gap-1 text-sm font-semibold text-falu hover:text-falu-hover no-underline">
              Öppna kartan <MapPin size={14} />
            </Link>
          </div>
        </div>
      </div>

      <footer data-testid="kallor-section" className="mt-12 pt-6 border-t border-warm-border">
        <h2 className="font-heading text-lg font-semibold text-ink mb-3">Källor</h2>
        <ul className="text-xs text-bark flex flex-col gap-1.5 leading-relaxed">
          <li>ICA Nära Backe/Rossön &ndash; <a href="https://www.ica.se/butiker/nara/stromsund/ica-nara-backe-1003552/" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">ica.se</a></li>
          <li>Byservicekontor &ndash; <a href="http://backe.nu/sv/byservicekontor" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">backe.nu</a></li>
          <li>Föreningar &ndash; <a href="https://www.stromsund.se/1008.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">stromsund.se</a></li>
          <li>DinTur tidtabeller &ndash; <a href="https://www.dintur.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">dintur.se</a></li>
        </ul>
        <p className="text-xs text-bark/50 mt-3">Senast kontrollerat: 20 april 2026.</p>
      </footer>
    </div>
  );
}

function InfoSection({ icon: Icon, title, children }) {
  return (
    <section data-testid={`section-${title.toLowerCase().replace(/\s/g, "-").replace(/[?]/g, "")}`} className="relative bg-parchment border border-warm-border rounded-sm p-6 sm:p-8 jugendstil-card">
      <div className="flex items-center gap-3 mb-4">
        <Icon size={20} className="text-pine" />
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">
          <span className="jugendstil-accent">{title}</span>
        </h2>
      </div>
      <div className="flex flex-col gap-3 text-sm text-ink/80 leading-relaxed">
        {children}
      </div>
    </section>
  );
}
